import moment from "moment";
import Store from "@/vuex/store";
import { parse_flight_offers_date } from "@liveflight/utilities/dates";
import { time_ranges } from "@liveflight/resources/filters";
import { get_availability } from "@/utilities/commons";
import { get_markup } from "@liveflight/utilities/flight-offer";

function get_filter_date_ranges(filter) {
  let from = 0,
    to = 0;

  // [morning] => 6:00 - 11:59
  if (filter == "morning") {
    from = 6;
    to = 12;
  }

  // [early_afternoon] => 12:00 - 14:59
  if (filter == "early_afternoon") {
    from = 12;
    to = 15;
  }

  // [morning] => 15:00 - 17:59
  if (filter == "late_afternoon") {
    from = 15;
    to = 18;
  }

  // [evening] => 18:00 - 5:59
  if (filter == "evening") {
    from = 18;
    to = 6;
  }

  return {
    from: from,
    to: to,
  };
}

/**
 * Filter definitions for liveflight.
 * Each key matches a liveflight store state property.
 * Properties:
 *   - label: display text for the filter tag
 *   - default_value: value used to reset the filter
 *   - is_active(state): returns true when the filter is actively narrowing results
 *
 * Separated into result_filters (client-side, no new search) and
 * search_trigger_filters (removing them emits change-search-by and triggers a new search).
 */

const result_filters = {
  filter_availability: {
    label: "In base al credito",
    default_value: false,
    is_active: (state) => state.filter_availability,
  },
  filter_carriers: {
    label: (state) => {
      const carriers = (state.results.dictionaries || {}).carriers || {};
      const names = state.filter_carriers.map(code => carriers[code] || code);
      return names.join(', ');
    },
    default_value: [],
    is_active: (state) => state.filter_carriers.length > 0,
  },
  filter_round_trip_departure: {
    label: (state) => `Andata: ${((time_ranges[state.filter_round_trip_departure] || {}).title || state.filter_round_trip_departure).toLowerCase()}`,
    default_value: "",
    is_active: (state) => !!state.filter_round_trip_departure,
  },
  filter_round_trip_arrival: {
    label: (state) => `Arrivo: ${((time_ranges[state.filter_round_trip_arrival] || {}).title || state.filter_round_trip_arrival).toLowerCase()}`,
    default_value: "",
    is_active: (state) => !!state.filter_round_trip_arrival,
  },
  filter_return_trip_departure: {
    label: (state) => `Ritorno: ${((time_ranges[state.filter_return_trip_departure] || {}).title || state.filter_return_trip_departure).toLowerCase()}`,
    default_value: "",
    is_active: (state) => !!state.filter_return_trip_departure,
  },
  filter_price_range: {
    label: (state) => {
      const r = state.filter_price_range;
      return `Prezzo: ${Math.floor(r.min)}€ – ${Math.ceil(r.max)}€`;
    },
    default_value: {},
    is_active: (state) => {
      const r = state.filter_price_range;
      const s = state.filter_price_settings;
      return r.min !== undefined && s.min !== undefined && (r.min !== s.min || r.max !== s.max);
    },
  },
  filter_included_bags: {
    label: "Bagagli inclusi",
    default_value: false,
    is_active: (state) => state.filter_included_bags,
  },
  filter_extra_bags: {
    label: "Bagagli extra",
    default_value: false,
    is_active: (state) => state.filter_extra_bags,
  },
};

const cabin_labels = {
  ECONOMY: "Economy",
  PREMIUM_ECONOMY: "Premium economy",
  BUSINESS: "Business",
  FIRST: "First",
};

const search_trigger_filters = {
  max_number_of_connections: {
    label: "Solo voli diretti",
    default_value: 2,
    is_active: (state) => String(state.max_number_of_connections) === "0",
  },
  included_checked_bags_only: {
    label: "Tariffe con bagagli in stiva",
    default_value: 0,
    is_active: (state) =>
      state.included_checked_bags_only === "1" ||
      state.included_checked_bags_only === 1,
  },
  cabin: {
    label: (state) => `Classe: ${cabin_labels[state.cabin] || state.cabin}`,
    default_value: "",
    is_active: (state) => !!state.cabin,
  },
};

/** All filter definitions combined. */
const filter_definitions = { ...result_filters, ...search_trigger_filters };

/** Default values for all filters, derived from filter_definitions. */
export const default_filters = Object.fromEntries(
  Object.entries(filter_definitions).map(([key, { default_value }]) => [
    key,
    default_value,
  ]),
);

/**
 * Resets a single filter to its default value.
 * For search_trigger_filters, also emits change-search-by to trigger a new search.
 * @param {string} key - The filter key matching a liveflight store state property (e.g. "filter_carriers")
 */
export const remove_filter = (key) => {
  const definition = filter_definitions[key];
  if (!definition) {
    return;
  }

  const previous_value = Store.state.liveflight[key];
  Store.commit(`liveflight/${key}`, definition.default_value);

  if (key in search_trigger_filters) {
    if (previous_value === definition.default_value) {
      return;
    }
    window.eventBus.$emit("change-search-by", {
      [key]: definition.default_value,
    });
  }
};

/**
 * Returns the list of currently active filters with their key and label.
 * @returns {Array<{key: string, label: string}>}
 */
export const get_active_filters = () => {
  const state = Store.state.liveflight;
  return Object.entries(filter_definitions)
    .filter(([, definition]) => definition.is_active(state))
    .map(([key, definition]) => ({ key, label: typeof definition.label === 'function' ? definition.label(state) : definition.label }));
};

export function filter_flight_offers() {
  let filter_carriers = Store.state.liveflight.filter_carriers,
    filter_round_trip_departure =
      Store.state.liveflight.filter_round_trip_departure,
    filter_round_trip_arrival =
      Store.state.liveflight.filter_round_trip_arrival,
    filter_return_trip_departure =
      Store.state.liveflight.filter_return_trip_departure,
    filtered_all_carriers = Store.state.liveflight.results.flights,
    filtered = Store.state.liveflight.results.flights,
    filter_included_bags = Store.state.liveflight.filter_included_bags,
    filter_extra_bags = Store.state.liveflight.filter_extra_bags;

  // filtro le flightoffer in base all'orario di partenza del volo di andata
  if (filter_round_trip_departure !== "") {
    let from = get_filter_date_ranges(filter_round_trip_departure).from,
      to = get_filter_date_ranges(filter_round_trip_departure).to;

    filtered = filtered.filter(function (f) {
      let date_object = parse_flight_offers_date(
        f.itineraries[0].segments[0].departure.at,
      ),
        date_hour = parseInt(date_object.hour.split(":")[0]),
        check = false;

      // caso SENZA mezzanotte nel range
      if (filter_round_trip_departure !== "evening") {
        check = date_hour >= from && date_hour < to;
      }
      // caso CON mezzanotte nel range
      if (filter_round_trip_departure == "evening") {
        check =
          (date_hour >= from && date_hour <= 23) ||
          (date_hour >= 0 && date_hour < to);
      }

      return check;
    });
  }

  // filtro le flightoffer in base all'orario di arrivo del volo di andata
  if (filter_round_trip_arrival !== "") {
    let from = get_filter_date_ranges(filter_round_trip_arrival).from,
      to = get_filter_date_ranges(filter_round_trip_arrival).to;

    filtered = filtered.filter(function (f) {
      let last_segment_index = f.itineraries[0].segments.length - 1,
        date_object = parse_flight_offers_date(
          f.itineraries[0].segments[last_segment_index].arrival.at,
        ),
        date_hour = parseInt(date_object.hour.split(":")[0]),
        check = false;

      // caso SENZA mezzanotte nel range
      if (filter_round_trip_arrival !== "evening") {
        check = date_hour >= from && date_hour < to;
      }
      // caso CON mezzanotte nel range
      if (filter_round_trip_arrival == "evening") {
        check =
          (date_hour >= from && date_hour <= 23) ||
          (date_hour >= 0 && date_hour < to);
      }

      return check;
    });
  }

  // filtro le flightoffer in base all'orario di partenza del volo di ritorno
  if (filter_return_trip_departure !== "") {
    let from = get_filter_date_ranges(filter_return_trip_departure).from,
      to = get_filter_date_ranges(filter_return_trip_departure).to;

    filtered = filtered.filter(function (f) {
      let date_object = parse_flight_offers_date(
        f.itineraries[f.itineraries.length - 1].segments[0].departure.at,
      ),
        date_hour = parseInt(date_object.hour.split(":")[0]),
        check = false;

      // caso SENZA mezzanotte nel range
      if (filter_return_trip_departure !== "evening") {
        check = date_hour >= from && date_hour < to;
      }
      // caso CON mezzanotte nel range
      if (filter_return_trip_departure == "evening") {
        check =
          (date_hour >= from && date_hour <= 23) ||
          (date_hour >= 0 && date_hour < to);
      }

      return check;
    });
  }

  if (filter_included_bags) {
    filtered = filtered.filter(function (f) {
      if (f.travelerPricings[0].fareDetailsBySegment[0].includedCheckedBags) {
        let icb =
          f.travelerPricings[0].fareDetailsBySegment[0].includedCheckedBags;
        if (icb.quantity && icb.quantity > 0) return true;
        if (icb.weight && icb.weight > 0) return true;
      }
      return false;
    });
  }

  if (filter_extra_bags) {
    filtered = filtered.filter(function (f) {
      let found = false;
      if (f.price.additionalServices) {
        f.price.additionalServices.forEach(function (as) {
          if (as.type == "CHECKED_BAGS") {
            found = true;
            return;
          }
        });
      }
      return found;
    });
  }

  // filtro in base al credito disponibile
  if (Store.state.liveflight.filter_availability) {
    const availability = get_availability();
    filtered = filtered.filter(f => availability.total >= Number(get_markup(f).mu_price));
  }

  // filtro in base al range di prezzo
  const price_range = Store.state.liveflight.filter_price_range;
  const price_settings = Store.state.liveflight.filter_price_settings;
  if (price_range.min !== undefined && price_settings.min !== undefined
      && (price_range.min !== price_settings.min || price_range.max !== price_settings.max)) {
    filtered = filtered.filter(f => {
      const price = Number(get_markup(f).mu_price);
      return price >= price_range.min && price <= price_range.max;
    });
  }

  // mi salvo i risultati filtrati PRIMA dell'applicazione del filtro per carriers, perché ho bisogno dei totali nella sidebar dei filtri
  filtered_all_carriers = filtered;

  // filter by carrier code => visualizzo le flightoffer che abbiano almeno un carrier code di quelli desiderati tra i segmenti del primo itinerario
  if (filter_carriers.length) {
    filtered = filtered.filter((f) =>
      filter_carriers.includes(f.itineraries[0].segments[0].carrierCode),
    );
  }

  return {
    filtered: filtered,
    filtered_all_carriers: filtered_all_carriers,
  };
}

/**
 * Formats a numeric time (hour) into a time string using moment.js
 * @param {number} time - The hour as a number (0-23)
 * @param {string} format - The format to use (default: 'HH:mm')
 * @returns {string} The formatted time string
 */
export const time_format = (time, format = "HH:mm") => {
  if (typeof time !== "number" || time < 0 || time > 23) {
    return "";
  }

  const hour = Math.floor(time);
  const minutes = Math.round((time - hour) * 60);

  return moment().hour(hour).minute(minutes).format(format);
};

export const is_direct_flight_filter_active = () => {
  let max_number_of_connections = "2";
  try {
    max_number_of_connections = String(
      Store.state.liveflight.max_number_of_connections,
    );
  } catch (e) {
    // EMPTY
  }
  return max_number_of_connections === "0";
};

export const set_direct_flight_filter_active = (selected) => {
  const max_number_of_connections = selected ? "0" : "2";
  Store.commit(
    "liveflight/max_number_of_connections",
    max_number_of_connections,
  );

  window.eventBus.$emit("change-search-by", {
    max_number_of_connections,
  });
};

/**
 * Resets all result filters to their default values.
 * Also resets the direct flight search-by filter via set_direct_flight_filter_active.
 */
export const remove_all_filters = () => {
  Object.keys(default_filters).forEach(remove_filter);
};
